import React, { useState, useEffect } from 'react';

interface Match {
  name: string;
  team1_name: string;
  team2_name: string;
  dateTimeGMT?: string;
  venue?: string;
  status?: string;
  winner?: string; // Add winner field
}

interface ManualScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (matches: Match[]) => void;
  availableTeams: Array<{ name: string; shortname: string }>;
}

const ManualScheduleModal: React.FC<ManualScheduleModalProps> = ({ isOpen, onClose, onAdd, availableTeams }) => {
  const [matches, setMatches] = useState<Match[]>([{
    name: '',
    team1_name: '',
    team2_name: '',
    dateTimeGMT: '',
    venue: '',
    status: 'upcoming',
    winner: '' // Add winner field
  }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-populate match name when teams are selected
  useEffect(() => {
    const updatedMatches = matches.map(match => {
      if (match.team1_name && match.team2_name) {
        return {
          ...match,
          name: `${match.team1_name} vs ${match.team2_name}`
        };
      }
      return match;
    });
    setMatches(updatedMatches);
  }, [matches.map(m => `${m.team1_name}-${m.team2_name}`).join(',')]);

  // Calculate status based on date/time
  const calculateStatus = (dateTimeString: string): string => {
    if (!dateTimeString) return 'upcoming';
    
    const matchDate = new Date(dateTimeString);
    const now = new Date();
    
    if (isNaN(matchDate.getTime())) return 'upcoming';
    
    const timeDiff = matchDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    
    if (hoursDiff < -24) return 'completed';
    if (hoursDiff < 0 && hoursDiff > -24) return 'live';
    return 'upcoming';
  };

  const addMatch = () => {
    setMatches([...matches, {
      name: '',
      team1_name: '',
      team2_name: '',
      dateTimeGMT: '',
      venue: '',
      status: 'upcoming',
      winner: '' // Add winner field
    }]);
  };

  const removeMatch = (index: number) => {
    if (matches.length > 1) {
      setMatches(matches.filter((_, i) => i !== index));
    }
  };

  const updateMatch = (index: number, field: keyof Match, value: string) => {
    const updatedMatches = [...matches];
    updatedMatches[index] = { ...updatedMatches[index], [field]: value };
    
    // Update status if date/time is being changed
    if (field === 'dateTimeGMT') {
      updatedMatches[index].status = calculateStatus(value);
      // If status changes away from completed, clear winner
      if (updatedMatches[index].status !== 'completed') {
        updatedMatches[index].winner = '';
      }
    }
    // If status is changed manually (future-proofing)
    if (field === 'status' && value !== 'completed') {
      updatedMatches[index].winner = '';
    }
    
    setMatches(updatedMatches);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const validMatches = matches.filter(match => 
      match.team1_name.trim() && match.team2_name.trim() && match.team1_name !== match.team2_name
    );
    
    if (validMatches.length === 0) {
      alert('Please add at least one match with different teams');
      return;
    }

    // Check for duplicate team combinations
    const teamCombinations = validMatches.map(m => `${m.team1_name}-${m.team2_name}`);
    const uniqueCombinations = new Set(teamCombinations);
    if (teamCombinations.length !== uniqueCombinations.size) {
      alert('Duplicate team combinations detected. Please ensure each match has unique teams.');
      return;
    }
    // Require winner for completed matches
    for (const match of validMatches) {
      if (match.status === 'completed' && !match.winner) {
        alert(`Please select a winner for the completed match: ${match.name}`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onAdd(validMatches);
      setMatches([{
        name: '',
        team1_name: '',
        team2_name: '',
        dateTimeGMT: '',
        venue: '',
        status: 'upcoming',
        winner: ''
      }]);
      onClose();
    } catch (error) {
      console.error('Error adding matches:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Schedule Manually</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {availableTeams.length === 0 && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded text-yellow-700">
            ⚠️ No teams available. Please add teams first in the Teams tab.
          </div>
        )}

        <div className="space-y-4">
          {matches.map((match, index) => (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Match {index + 1}</h3>
                {matches.length > 1 && (
                  <button
                    onClick={() => removeMatch(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team 1 *
                  </label>
                  <select
                    value={match.team1_name}
                    onChange={(e) => updateMatch(index, 'team1_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Team 1</option>
                    {availableTeams.map((team) => (
                      <option key={team.name} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team 2 *
                  </label>
                  <select
                    value={match.team2_name}
                    onChange={(e) => updateMatch(index, 'team2_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Team 2</option>
                    {availableTeams.map((team) => (
                      <option key={team.name} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Match Name (Auto-generated)
                  </label>
                  <input
                    type="text"
                    value={match.name}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    placeholder="Will be auto-generated"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date & Time (GMT)
                  </label>
                  <input
                    type="datetime-local"
                    value={match.dateTimeGMT || ''}
                    onChange={(e) => updateMatch(index, 'dateTimeGMT', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status (Auto-calculated)
                  </label>
                  <input
                    type="text"
                    value={match.status || 'upcoming'}
                    readOnly
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 ${
                      match.status === 'live' ? 'text-orange-600' :
                      match.status === 'completed' ? 'text-green-600' :
                      'text-blue-600'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Venue
                  </label>
                  <input
                    type="text"
                    value={match.venue || ''}
                    onChange={(e) => updateMatch(index, 'venue', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Wankhede Stadium"
                  />
                </div>
              </div>

              {/* Winner dropdown if completed */}
              {match.status === 'completed' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Winner *
                  </label>
                  <select
                    value={match.winner || ''}
                    onChange={e => updateMatch(index, 'winner', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Winner</option>
                    <option value={match.team1_name}>{match.team1_name}</option>
                    <option value={match.team2_name}>{match.team2_name}</option>
                  </select>
                </div>
              )}

              {match.team1_name && match.team2_name && match.team1_name === match.team2_name && (
                <div className="mt-2 p-2 bg-red-100 border border-red-400 rounded text-red-700 text-sm">
                  ⚠️ Team 1 and Team 2 cannot be the same
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <button
            onClick={addMatch}
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 mr-2"
            disabled={availableTeams.length < 2}
          >
            + Add Another Match
          </button>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || availableTeams.length < 2}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Add Matches'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualScheduleModal; 